<template>
  <div>
    <div v-if="initial">
      <b-breadcrumb size="is-large" separator="has-succeeds-separator" aria-label="breadcrumbs">
        <b-breadcrumb-item tag="a" @click.prevent="goToList()">
          {{ $t('integrations.title') }}
        </b-breadcrumb-item>
        <b-breadcrumb-item tag="span" active>
          <InlineNameEditor
            :model-value="(initial as any).name || integrationId"
            :can-edit="!!initial"
            :placeholder="$t('integrations.name')"
            :validate="validateIntegrationName"
            :save="saveIntegrationName"
          />
        </b-breadcrumb-item>
      </b-breadcrumb>
      <component :is="activeComponent" :id="integrationId" :initial="initial" @saved="goToList" />
    </div>
    <div v-else>
      <div class="has-text-centered">
        <p class="is-size-4">{{ $t('integrations.notFound') }}</p>
        <b-button @click="goToList()" class="mt-4">
          {{ $t('global.back') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, getCurrentInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import InlineNameEditor from '@/components/InlineNameEditor.vue'
import { useIntegrationTemplates } from '@/utils/integrationTemplates'

export default defineComponent({
  name: 'IntegrationEdit',
  components: { InlineNameEditor },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const { getTemplateComponent } = useIntegrationTemplates()
    const vm = getCurrentInstance()
    const proxy = vm?.proxy as any
    
    const integrationId = computed(() => route.params.id as string)
    const type = ref('')
    const initial = ref<Record<string, unknown> | null>(null)
    const loaded = ref(false)

    const load = async () => {
      await store.dispatch('loadModel', { model: 'integrations' })
      const item = (store.state.integrations as any)[integrationId.value]
      if (item) {
        type.value = item.type
        initial.value = item.content
      }
      loaded.value = true
    }

    const activeComponent = computed(() => getTemplateComponent(type.value))

    const validateIntegrationName = async (name: string): Promise<string | null> => {
      // Basic format validation: letters, digits, dashes, underscores; <= 64
      if (!/^[A-Za-z0-9_-]{1,64}$/.test(name)) {
        return proxy?.$t ? proxy.$t('integrations.nameValidationError') : 'Invalid name'
      }
      // Check duplicates (exclude current id)
      const exists = await store.dispatch('checkIntegrationNameExists', { name, excludeId: integrationId.value })
      if (exists) {
        return proxy?.$t ? proxy.$t('integrations.nameDuplicateError') : 'Duplicate name'
      }
      return null
    }

    const saveIntegrationName = async (name: string) => {
      if (!initial.value) return
      try {
        const body = { ...(initial.value as Record<string, unknown>), name } as any
        await store.dispatch('upsertIntegration', { id: integrationId.value, body })
        await load()
      } catch (e) {
        // ignore; InlineNameEditor displays error
      }
    }

    const goToList = () => router.push({name: 'integration-list'})

    onMounted(load)
    return { integrationId, activeComponent, initial, loaded, goToList, validateIntegrationName, saveIntegrationName }
  }
})
</script>


